import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronDown, Menu, X, Gamepad2, Download } from "lucide-react";
import { useInstallApp } from "./InstallAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [shows, setShows] = useState([]);
  const [games, setGames] = useState([]);
  const [navbarBanner, setNavbarBanner] = useState(null);
  const [showsDropdownOpen, setShowsDropdownOpen] = useState(false);
  const [gamesDropdownOpen, setGamesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileShowsOpen, setMobileShowsOpen] = useState(false);
  const [mobileGamesOpen, setMobileGamesOpen] = useState(false);
  const dropdownTimeoutRef = useRef(null);
  const gamesDropdownTimeoutRef = useRef(null);
  
  // PWA Install hook
  const { canInstall, install } = useInstallApp();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileShowsOpen(false);
    setMobileGamesOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    fetchShows();
    fetchGames();
    fetchNavbarBanner();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchShows = async () => {
    try {
      const res = await axios.get(`${API}/shows`);
      setShows(res.data);
    } catch (error) {
      console.error("Error fetching shows:", error);
    }
  };

  const fetchGames = async () => {
    try {
      const res = await axios.get(`${API}/games`);
      setGames(res.data.filter(g => g.active));
    } catch (error) {
      console.error("Error fetching games:", error);
    }
  };

  const fetchNavbarBanner = async () => {
    try {
      const res = await axios.get(`${API}/navbar-banner`);
      if (res.data && res.data.active && res.data.image_url) {
        setNavbarBanner(res.data);
      }
    } catch (error) {
      console.error("Error fetching navbar banner:", error);
    }
  };

  // Handle show click - skip to episodes if only 1 season
  const handleShowClick = async (e, showIdOrSlug) => {
    e.preventDefault();
    setShowsDropdownOpen(false);
    setMobileMenuOpen(false);
    
    try {
      // First get the show to obtain its actual ID
      const showRes = await axios.get(`${API}/shows/${showIdOrSlug}`);
      const show = showRes.data;
      const res = await axios.get(`${API}/seasons?show_id=${show.id}`);
      const seasons = res.data;
      
      if (seasons.length === 1) {
        // Skip directly to episodes
        navigate(`/?season=${seasons[0].slug || seasons[0].id}`);
      } else {
        // Show seasons list
        navigate(`/?show=${showIdOrSlug}`);
      }
    } catch (error) {
      // Fallback to show page
      navigate(`/?show=${showIdOrSlug}`);
    }
  };

  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setShowsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowsDropdownOpen(false);
    }, 300);
  };

  return (
    <nav 
      className={`navbar ${scrolled ? "scrolled" : ""}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between">
        {/* Left side - Logo + Nav Links */}
        <div className="flex items-center gap-8">
          {/* Logo with Icon */}
          <a 
            href="/"
            data-testid="logo"
            className="flex items-center gap-3 py-2 px-1"
          >
            <img 
              src="https://hosting.photobucket.com/04823986-54d7-48c7-8bb0-90b2e37217f1/a456f9a0-d628-461a-acf4-136c747b7656.png"
              alt="Darkar Logo"
              className="h-10 md:h-12 w-auto"
            />
            <span className="logo-custom hidden sm:inline">DARKAR COMPANY STUDIOS</span>
            <span className="logo-custom sm:hidden text-xs">DARKAR COMPANY</span>
          </a>

          {/* Desktop Nav Links */}
          <div className="hidden sm:flex items-center gap-6">
            <Link 
              to="/"
              className="text-white/80 hover:text-[#E91E8C] transition-colors font-bold text-lg"
              data-testid="nav-home"
            >
              Inicio
            </Link>
            
            {/* Shows Dropdown - Desktop */}
            <div 
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                type="button"
                className={`flex items-center gap-1 transition-colors font-bold text-lg ${showsDropdownOpen ? 'text-[#E91E8C]' : 'text-white/80 hover:text-[#E91E8C]'}`}
                data-testid="nav-shows"
              >
                Shows
                <ChevronDown size={18} className={`transition-transform ${showsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
            
            {showsDropdownOpen && (
              <div className="absolute top-full right-0 pt-2 z-50">
                <div 
                  className="w-56 bg-black border-2 border-white/20 shadow-xl"
                  style={{ borderRadius: '4px' }}
                >
                  {shows.length === 0 ? (
                    <div className="px-4 py-3 text-gray-400 text-sm">
                      No hay shows disponibles
                    </div>
                  ) : (
                    <div className="py-2">
                      {shows.map((show) => (
                        <a
                          key={show.id}
                          href={`/?show=${show.slug || show.id}`}
                          onClick={(e) => handleShowClick(e, show.slug || show.id)}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-[#E91E8C] transition-colors group cursor-pointer"
                          data-testid={`nav-show-${show.id}`}
                        >
                          {show.thumbnail && (
                            <img 
                              src={show.thumbnail} 
                              alt={show.title}
                              className="w-8 h-12 object-cover rounded flex-shrink-0"
                            />
                          )}
                          <span className="text-white text-sm font-medium truncate">
                            {show.title}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>

            {/* Games Dropdown - Desktop */}
            <div 
              className="relative"
              onMouseEnter={() => {
                if (gamesDropdownTimeoutRef.current) clearTimeout(gamesDropdownTimeoutRef.current);
                setGamesDropdownOpen(true);
              }}
              onMouseLeave={() => {
                gamesDropdownTimeoutRef.current = setTimeout(() => setGamesDropdownOpen(false), 300);
              }}
            >
              <button
                type="button"
                className={`flex items-center gap-1 transition-colors font-bold text-lg ${gamesDropdownOpen ? 'text-[#E91E8C]' : 'text-white/80 hover:text-[#E91E8C]'}`}
                onClick={() => navigate('/juegos')}
                data-testid="nav-games"
              >
                Juegos
                {games.length > 0 && <ChevronDown size={18} className={`transition-transform ${gamesDropdownOpen ? 'rotate-180' : ''}`} />}
              </button>
              
              {gamesDropdownOpen && games.length > 0 && (
                <div className="absolute top-full right-0 pt-2 z-50">
                  <div 
                    className="w-56 bg-black border-2 border-white/20 shadow-xl"
                    style={{ borderRadius: '4px' }}
                  >
                    <div className="py-2">
                      {games.map((game) => (
                        <Link
                          key={game.id}
                          to={`/juegos/${game.slug || game.id}`}
                          className="flex items-center gap-3 px-4 py-2 hover:bg-[#E91E8C] transition-colors group cursor-pointer"
                          onClick={() => setGamesDropdownOpen(false)}
                          data-testid={`nav-game-${game.id}`}
                        >
                          {game.thumbnail_url ? (
                            <img 
                              src={game.thumbnail_url} 
                              alt={game.title}
                              className="w-8 h-12 object-cover rounded flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-12 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                              <Gamepad2 size={16} className="text-white/50" />
                            </div>
                          )}
                          <span className="text-white text-sm font-medium truncate">
                            {game.title}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Music Link - Desktop */}
            <Link 
              to="/musica"
              className="text-white/80 hover:text-[#E91E8C] transition-colors font-bold text-lg"
              data-testid="nav-music"
            >
              Música
            </Link>

            {/* Stickers Link - Desktop (Hidden until app is in stores)
            <Link 
              to="/stickers"
              className="text-white/80 hover:text-[#25D366] transition-colors font-bold text-lg"
              data-testid="nav-stickers"
            >
              Stickers
            </Link>
            */}

            {/* Shop Link - Desktop */}
            <a 
              href="http://darkar.co"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-[#E91E8C] transition-colors font-bold text-lg"
              data-testid="nav-shop"
            >
              Tienda
            </a>
          </div>
        </div>

        {/* Right side - Navbar Banner (Desktop) */}
        {navbarBanner && (
          <div className="hidden lg:block">
            {navbarBanner.link_url ? (
              <a 
                href={navbarBanner.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block hand-drawn-border-white rounded overflow-hidden hover:opacity-90 transition-opacity"
                data-testid="navbar-banner-link"
              >
                <img
                  src={navbarBanner.image_url}
                  alt="Banner"
                  className="h-[40px] w-auto"
                  style={{ maxWidth: '300px' }}
                />
              </a>
            ) : (
              <div className="hand-drawn-border-white rounded overflow-hidden">
                <img
                  src={navbarBanner.image_url}
                  alt="Banner"
                  className="h-[40px] w-auto"
                  style={{ maxWidth: '300px' }}
                />
              </div>
            )}
          </div>
        )}

        {/* Mobile Menu Button */}
        <div className="sm:hidden flex items-center">
          <button
            type="button"
            className="text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden absolute top-full left-0 right-0 bg-black border-t border-white/10 z-50">
          <div className="px-4 py-4 space-y-2">
            {/* Navbar Banner - Mobile (at top) */}
            {navbarBanner && (
              <div className="mb-4">
                {navbarBanner.link_url ? (
                  <a 
                    href={navbarBanner.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block hand-drawn-border-white rounded overflow-hidden"
                    data-testid="navbar-banner-mobile-link"
                  >
                    <img
                      src={navbarBanner.image_url}
                      alt="Banner"
                      className="w-full h-auto"
                    />
                  </a>
                ) : (
                  <div className="hand-drawn-border-white rounded overflow-hidden">
                    <img
                      src={navbarBanner.image_url}
                      alt="Banner"
                      className="w-full h-auto"
                    />
                  </div>
                )}
              </div>
            )}

            <Link 
              to="/"
              className="block py-3 px-4 text-white font-medium hover:bg-[#E91E8C] rounded-lg transition-colors"
              data-testid="mobile-nav-home"
            >
              Inicio
            </Link>
            
            {/* Shows Accordion - Mobile */}
            <div>
              <button
                type="button"
                className="w-full flex items-center justify-between py-3 px-4 text-white font-medium hover:bg-white/10 rounded-lg transition-colors"
                onClick={() => setMobileShowsOpen(!mobileShowsOpen)}
                data-testid="mobile-nav-shows"
              >
                Shows
                <ChevronDown size={20} className={`transition-transform ${mobileShowsOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileShowsOpen && (
                <div className="mt-2 ml-4 space-y-1">
                  {shows.length === 0 ? (
                    <p className="text-gray-400 text-sm py-2 px-4">No hay shows disponibles</p>
                  ) : (
                    shows.map((show) => (
                      <a
                        key={show.id}
                        href={`/?show=${show.slug || show.id}`}
                        onClick={(e) => handleShowClick(e, show.slug || show.id)}
                        className="flex items-center gap-3 py-2 px-4 text-white/80 hover:text-[#E91E8C] hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                        data-testid={`mobile-nav-show-${show.id}`}
                      >
                        {show.thumbnail && (
                          <img 
                            src={show.thumbnail} 
                            alt={show.title}
                            className="w-8 h-12 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <span className="text-sm font-medium">{show.title}</span>
                      </a>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Games Accordion - Mobile */}
            <div>
              <button
                type="button"
                className="w-full flex items-center justify-between py-3 px-4 text-white font-medium hover:bg-white/10 rounded-lg transition-colors"
                onClick={() => setMobileGamesOpen(!mobileGamesOpen)}
                data-testid="mobile-nav-games"
              >
                Juegos
                <ChevronDown size={20} className={`transition-transform ${mobileGamesOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {mobileGamesOpen && (
                <div className="mt-2 ml-4 space-y-1">
                  {games.length === 0 ? (
                    <p className="text-gray-400 text-sm py-2 px-4">No hay juegos disponibles</p>
                  ) : (
                    <>
                      {games.map((game) => (
                        <Link
                          key={game.id}
                          to={`/juegos/${game.slug || game.id}`}
                          className="flex items-center gap-3 py-2 px-4 text-white/80 hover:text-[#E91E8C] hover:bg-white/5 rounded-lg transition-colors"
                          onClick={() => setMobileMenuOpen(false)}
                          data-testid={`mobile-nav-game-${game.id}`}
                        >
                          {game.thumbnail_url ? (
                            <img 
                              src={game.thumbnail_url} 
                              alt={game.title}
                              className="w-8 h-12 object-cover rounded flex-shrink-0"
                            />
                          ) : (
                            <div className="w-8 h-12 bg-white/10 rounded flex items-center justify-center flex-shrink-0">
                              <Gamepad2 size={14} className="text-white/50" />
                            </div>
                          )}
                          <span className="text-sm font-medium">{game.title}</span>
                        </Link>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Music Link - Mobile */}
            <Link 
              to="/musica"
              className="block py-3 px-4 text-white font-medium hover:bg-[#E91E8C] rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-music"
            >
              Música
            </Link>

            {/* Stickers Link - Mobile (Hidden until app is in stores)
            <Link 
              to="/stickers"
              className="block py-3 px-4 text-white font-medium hover:bg-[#25D366] rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-stickers"
            >
              Stickers
            </Link>
            */}

            {/* Shop Link - Mobile */}
            <a 
              href="http://darkar.co"
              target="_blank"
              rel="noopener noreferrer"
              className="block py-3 px-4 text-white font-medium hover:bg-[#E91E8C] rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-shop"
            >
              Tienda
            </a>

            {/* Install App Button - Mobile Only */}
            {canInstall && (
              <button
                onClick={() => {
                  install();
                  setMobileMenuOpen(false);
                }}
                className="w-full flex items-center gap-3 py-3 px-4 mt-4 bg-[#FFE500] text-black font-bold rounded-lg transition-colors hover:bg-[#FFE500]/80"
                data-testid="mobile-nav-install"
              >
                <Download size={20} />
                Instalar App
              </button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
